import { create } from 'zustand';
import { Place, PlaceCategory } from '@/types/places';
import type { Database } from '@/integrations/supabase/types';

type KosherStatus = Database['public']['Enums']['kosher_status'];

export type BottomSheetState = 'minimized' | 'collapsed' | 'half' | 'full';

interface MapState {
  center: [number, number];
  zoom: number;
  selectedPlace: Place | null;
  activeCategories: PlaceCategory[];
  activeKosherStatuses: KosherStatus[];
  activeKosherAuthorities: string[];
  searchQuery: string;
  showNearbyPanel: boolean;
  showPlaceDetail: boolean;
  favorites: string[];
  userLocation: [number, number] | null;
  isLocating: boolean;
  locationError: string | null;
  bottomSheetState: BottomSheetState;
  highlightedPlaceId: string | null;
  closePopupSignal: number;
  
  // Actions
  setCenter: (center: [number, number]) => void;
  setZoom: (zoom: number) => void;
  setSelectedPlace: (place: Place | null) => void;
  toggleCategory: (category: PlaceCategory) => void;
  toggleKosherStatus: (status: KosherStatus) => void;
  toggleKosherAuthority: (authority: string) => void;
  resetFilters: () => void;
  setSearchQuery: (query: string) => void;
  toggleNearbyPanel: () => void;
  togglePlaceDetail: () => void;
  toggleFavorite: (placeId: string) => void;
  isFavorite: (placeId: string) => boolean;
  setUserLocation: (location: [number, number] | null) => void;
  setIsLocating: (isLocating: boolean) => void;
  setLocationError: (error: string | null) => void;
  setBottomSheetState: (state: BottomSheetState) => void;
  setHighlightedPlaceId: (id: string | null) => void;
  triggerClosePopup: () => void;
}

export const useMapStore = create<MapState>((set, get) => ({
  // Default center: Paris, France
  center: [2.3522, 48.8566],
  zoom: 13,
  selectedPlace: null,
  activeCategories: [],
  activeKosherStatuses: [],
  activeKosherAuthorities: [],
  searchQuery: '',
  showNearbyPanel: true,
  showPlaceDetail: false,
  favorites: [],
  userLocation: null,
  isLocating: false,
  locationError: null,
  bottomSheetState: 'collapsed',
  highlightedPlaceId: null,
  closePopupSignal: 0,

  setCenter: (center) => set({ center }),
  setZoom: (zoom) => set({ zoom }),
  setSelectedPlace: (place) => set({ selectedPlace: place, showPlaceDetail: !!place }),
  toggleCategory: (category) => set((state) => ({
    activeCategories: state.activeCategories.includes(category)
      ? state.activeCategories.filter(c => c !== category)
      : [...state.activeCategories, category]
  })),
  toggleKosherStatus: (status) => set((state) => ({
    activeKosherStatuses: state.activeKosherStatuses.includes(status)
      ? state.activeKosherStatuses.filter(s => s !== status)
      : [...state.activeKosherStatuses, status]
  })),
  toggleKosherAuthority: (authority) => set((state) => ({
    activeKosherAuthorities: state.activeKosherAuthorities.includes(authority)
      ? state.activeKosherAuthorities.filter(a => a !== authority)
      : [...state.activeKosherAuthorities, authority]
  })),
  resetFilters: () => set({ activeCategories: [], activeKosherStatuses: [], activeKosherAuthorities: [] }),
  setSearchQuery: (query) => set({ searchQuery: query }),
  toggleNearbyPanel: () => set((state) => ({ showNearbyPanel: !state.showNearbyPanel })),
  togglePlaceDetail: () => set((state) => ({ showPlaceDetail: !state.showPlaceDetail })),
  toggleFavorite: (placeId) => set((state) => ({
    favorites: state.favorites.includes(placeId)
      ? state.favorites.filter(id => id !== placeId)
      : [...state.favorites, placeId]
  })),
  isFavorite: (placeId) => get().favorites.includes(placeId),
  setUserLocation: (location) => set({ userLocation: location }),
  setIsLocating: (isLocating) => set({ isLocating }),
  setLocationError: (error) => set({ locationError: error }),
  setBottomSheetState: (state) => set({ bottomSheetState: state }),
  setHighlightedPlaceId: (id) => set({ highlightedPlaceId: id }),
  triggerClosePopup: () => set((state) => ({ closePopupSignal: state.closePopupSignal + 1 })),
}));
